import { useState } from 'react'
import clsx from 'clsx'
import { InfoTooltip } from '@/components/InfoTooltip'
import type { ConcreteOverridableField } from '../overrideUtils'

export interface OverrideValueInputCellProps {
  field: ConcreteOverridableField
  value: number | string | boolean | undefined
  onChange: (value: number) => void
  serverError?: string
}

// Inline bounds validation is client-side UX only (mirrors product-types'
// LimitsSection and PRD 02's own precedent) - the server (override.py's own
// bounds/tightening-only checks, re-run on every PUT) is the real gate
// regardless. Every real allow-listed field today is numeric; the input
// assumes that shape rather than a generic string editor.
export function OverrideValueInputCell({ field, value, onChange, serverError }: OverrideValueInputCellProps) {
  const [touched, setTouched] = useState(false)
  const numericValue = typeof value === 'number' ? value : 0

  const belowMin = field.bound?.min !== undefined && numericValue < field.bound.min
  const aboveMax = field.bound?.max !== undefined && numericValue > field.bound.max
  const outOfBounds = touched && (belowMin || aboveMax)

  const message = serverError ?? (outOfBounds ? (aboveMax ? `Maximum allowed: ${field.bound?.max}` : `Minimum allowed: ${field.bound?.min}`) : undefined)

  return (
    <div className="flex flex-col gap-1">
      <input
        type="number"
        value={numericValue}
        onChange={(event) => onChange(Number(event.target.value))}
        onBlur={() => setTouched(true)}
        className={clsx(
          'w-28 rounded-control border bg-bg px-3 py-2 text-body text-text-primary outline-none',
          message ? 'border-danger' : 'border-border',
        )}
      />
      {field.bound && (field.bound.min !== undefined || field.bound.max !== undefined) && (
        <span className="flex items-center gap-1 text-meta text-text-secondary">
          {[
            field.bound.min !== undefined ? `Min ${field.bound.min}` : undefined,
            field.bound.max !== undefined ? `Max ${field.bound.max}` : undefined,
          ]
            .filter(Boolean)
            .join(' · ')}
          <InfoTooltip text="The platform sets this limit. Some fields can only be lowered here, never raised, even if the platform's own maximum is higher." />
        </span>
      )}
      {message && <span className="text-meta text-danger">{message}</span>}
    </div>
  )
}
